import { getIdToken } from "../auth";
import type {
  ArtifactBundle,
  CanvasSession,
  CanvasVersionDoc,
  Complexity,
  ConsistencyResult,
  DeploymentStage,
  FieldAnswer,
  PhaseCode,
  ReviewLayer,
  ReviewDelta,
  ReviewState,
  SignedArtifact,
  SignedBespokeReport,
  Stage,
  VariantKind,
  VersionSummary,
} from "./schema";

function apiBase(): string {
  const url = import.meta.env.VITE_API_FUNCTION_URL as string | undefined;
  if (!url) throw new Error("VITE_API_FUNCTION_URL is not set.");
  return url.replace(/\/$/, "");
}

async function headers(): Promise<HeadersInit> {
  const token = await getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function check(res: Response): Promise<void> {
  if (!res.ok) {
    let detail = "";
    let code: string | undefined;
    try {
      const body = await res.json();
      detail = body?.error ?? JSON.stringify(body);
      code = body?.code;
    } catch {
      detail = await res.text().catch(() => "");
    }
    // Preserve status + machine code (e.g. CARD_REQUIRED) for callers that
    // branch on them, while keeping the human message on Error.message.
    throw Object.assign(new Error(`API ${res.status}: ${detail}`), { status: res.status, code });
  }
}

export interface CanvasSummary {
  id: string;
  workflowName: string;
  namedOwner: string;
  currentStage: Stage;
  updatedAt: number;
  hasArtifacts: boolean;
  seeded?: boolean;
  version?: number;
  versionCount?: number;
  reviewInProgress?: boolean;
}

export async function createCanvas(args: {
  workflowName: string;
  namedOwner: string;
  industry: string;
  deploymentStage: DeploymentStage;
  complexity: Complexity;
}): Promise<{ id: string }> {
  const res = await fetch(`${apiBase()}/canvas`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args),
  });
  await check(res);
  return res.json();
}

export async function seedCanvas(args?: {
  workflowName?: string;
  namedOwner?: string;
  industry?: string;
  deploymentStage?: DeploymentStage;
  complexity?: Complexity;
}): Promise<{ id: string }> {
  const res = await fetch(`${apiBase()}/canvas/seed`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args ?? {}),
  });
  await check(res);
  return res.json();
}

export interface HealthInfo {
  defaultProvider: string;
  defaultModel: string | null;
}

export async function getHealth(): Promise<HealthInfo> {
  const res = await fetch(`${apiBase()}/health`);
  await check(res);
  const data = (await res.json()) as HealthInfo;
  return data;
}

export async function listCanvases(): Promise<CanvasSummary[]> {
  const res = await fetch(`${apiBase()}/canvas`, { headers: await headers() });
  await check(res);
  const data = (await res.json()) as { sessions: CanvasSummary[] };
  return data.sessions;
}

export async function deleteCanvas(id: string): Promise<void> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: await headers(),
  });
  await check(res);
}

export async function getCanvas(id: string): Promise<CanvasSession> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}`, {
    headers: await headers(),
  });
  await check(res);
  const data = (await res.json()) as { session: CanvasSession };
  return data.session;
}

export interface SubmitAnswerArgs {
  phase: PhaseCode;
  fieldIndex: number;
  answer: string;
  evalText: string;
  score: number;
  gapFlagged: boolean;
  gapNote: string;
  confidence: "low" | "medium" | "high";
  advance: boolean;
  pushbackPass?: number;
  acceptedAsGap?: boolean;
  source?: "document_upload" | "document_upload_edited";
  scoringModel?: string;
}

export async function submitAnswer(
  id: string,
  args: SubmitAnswerArgs
): Promise<{
  nextStage: Stage;
  nextFieldIndex: number;
  phaseComplete: boolean;
  qualityScore: number | null;
}> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}/answer`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args),
  });
  await check(res);
  return res.json();
}

export interface SubmitDRArgs {
  drIndex: number;
  answer: string;
  evalText: string;
  score: number;
  gapFlagged: boolean;
  gapNote: string;
  confidence: "low" | "medium" | "high";
  advance: boolean;
  scoringModel?: string;
}

export async function submitDR(
  id: string,
  args: SubmitDRArgs
): Promise<{ nextStage: Stage; nextFieldIndex: number; drComplete: boolean }> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}/dr-answer`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args),
  });
  await check(res);
  return res.json();
}

export async function advancePhase(
  id: string,
  args: { phase: PhaseCode }
): Promise<{ nextStage: Stage; nextFieldIndex: number }> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}/advance`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args),
  });
  await check(res);
  return res.json();
}

export async function jumpTo(
  id: string,
  args: { phase: PhaseCode; fieldIndex: number }
): Promise<{ nextStage: Stage; nextFieldIndex: number }> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}/jump`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args),
  });
  await check(res);
  return res.json();
}

export async function skipField(
  id: string,
  args: { phase: PhaseCode; fieldIndex: number; reason: string }
): Promise<{ nextStage: Stage; nextFieldIndex: number; phaseComplete: boolean }> {
  const res = await fetch(`${apiBase()}/canvas/${encodeURIComponent(id)}/skip`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(args),
  });
  await check(res);
  return res.json();
}

export async function runConsistency(id: string): Promise<ConsistencyResult[]> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(id)}/consistency`,
    { method: "POST", headers: await headers() }
  );
  await check(res);
  const data = (await res.json()) as { results: ConsistencyResult[] };
  return data.results;
}

export interface OutputsResponse {
  grade: string;
  composite: number;
  canvasJson: Record<string, unknown>;
  docxBase64: string;
  baselineRecordBase64: string;
  artifacts: ArtifactBundle;
}

export async function generateOutputs(id: string): Promise<OutputsResponse> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(id)}/outputs`,
    { headers: await headers() }
  );
  await check(res);
  return res.json();
}

export async function getArtifacts(id: string): Promise<ArtifactBundle | null> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(id)}/artifacts`,
    { headers: await headers() }
  );
  await check(res);
  const body = (await res.json()) as ArtifactBundle | { ready: false };
  if ("ready" in body && body.ready === false) return null;
  return body as ArtifactBundle;
}

export async function generateReportVariant(
  id: string,
  variant: VariantKind
): Promise<SignedArtifact> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(id)}/report/${encodeURIComponent(variant)}`,
    { method: "POST", headers: await headers() }
  );
  await check(res);
  return res.json();
}

export async function generateCustomReport(
  id: string,
  prompt: string
): Promise<SignedBespokeReport> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(id)}/report/custom`,
    { method: "POST", headers: await headers(), body: JSON.stringify({ prompt }) }
  );
  await check(res);
  return res.json();
}

export async function listVersions(
  sessionId: string
): Promise<{ versions: VersionSummary[] }> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/versions`,
    { headers: await headers() }
  );
  await check(res);
  return res.json();
}

export async function getVersion(
  sessionId: string,
  version: number
): Promise<CanvasVersionDoc> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/versions/${version}`,
    { headers: await headers() }
  );
  if (res.status === 404) {
    throw new Error("Version not found");
  }
  await check(res);
  return res.json();
}

// ---------------------------------------------------------------------------
// Phase 10 — Canvas review (/review/*)
// ---------------------------------------------------------------------------

export async function startReview(
  sessionId: string,
  startLayer?: ReviewLayer
): Promise<{ review: ReviewState }> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/review/start`,
    {
      method: "POST",
      headers: await headers(),
      body: JSON.stringify(startLayer ? { startLayer } : {}),
    }
  );
  await check(res);
  return res.json();
}

export async function getReview(
  sessionId: string
): Promise<{ review: ReviewState } | null> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/review`,
    { headers: await headers() }
  );
  if (res.status === 404) return null;
  await check(res);
  return res.json();
}

export interface EditReviewFieldArgs {
  fieldName: string;
  answer: string;
  score: number;
  gapFlagged: boolean;
  gapNote: string;
  confidence: "low" | "medium" | "high";
  evalText: string;
  scoringModel?: string;
}

export async function editReviewField(
  sessionId: string,
  code: ReviewLayer,
  body: EditReviewFieldArgs
): Promise<{ field: FieldAnswer }> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/review/layer/${encodeURIComponent(code)}/edit-field`,
    {
      method: "POST",
      headers: await headers(),
      body: JSON.stringify(body),
    }
  );
  await check(res);
  return res.json();
}

export async function advanceReviewLayer(
  sessionId: string,
  code: ReviewLayer
): Promise<{ review: ReviewState; readyToFinalize: boolean }> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/review/layer/${encodeURIComponent(code)}/advance`,
    { method: "POST", headers: await headers() }
  );
  await check(res);
  return res.json();
}

export async function cancelReview(
  sessionId: string
): Promise<{ ok: boolean }> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/review/cancel`,
    { method: "POST", headers: await headers() }
  );
  await check(res);
  return res.json();
}

export async function finalizeReview(sessionId: string): Promise<{
  ok: boolean;
  newVersion: number;
  delta: ReviewDelta;
  artifacts: ArtifactBundle;
}> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/review/finalize`,
    { method: "POST", headers: await headers() }
  );
  await check(res);
  return res.json();
}

export async function getVersionArtifacts(
  sessionId: string,
  version: number
): Promise<ArtifactBundle> {
  const res = await fetch(
    `${apiBase()}/canvas/${encodeURIComponent(sessionId)}/versions/${version}/artifacts`,
    { headers: await headers() }
  );
  await check(res);
  return res.json();
}
